import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAuctionSocket } from "@/lib/useAuctionSocket";
import Marquee from "react-fast-marquee";
import Modal from "@/components/Modal";
import PlayerPhoto from "@/components/PlayerPhoto";
import { resolveImageUrl } from "@/components/ImageInput";
import { brandLogoFor } from "@/pages/overlays/_shared";
import { Play, Pause, Stop, SkipForward, HandArrowUp, ArrowsClockwise, Trophy, ArrowLeft, Timer, XCircle, ArrowUUpLeft, DotsThreeVertical, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const PHOTO_FALLBACK = "/player-placeholder.svg";

function HistoryActions({ player, onReauction, onUnsold, onAvailable }) {
  const isSold = player._kind === "sold";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title="Edit — Re-auction / Mark Unsold / Available"
          className="p-2 rounded hover:bg-gray-100 border border-gray-300 flex-shrink-0"
          data-testid={`history-actions-${player.id}`}
        >
          <DotsThreeVertical size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border-gray-200 text-slate-900">
        <DropdownMenuLabel className="text-xs font-bold uppercase tracking-widest text-gray-500">
          {player.name}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-gray-100" />
        <DropdownMenuItem
          onClick={onReauction}
          className="cursor-pointer focus:bg-emerald-500/20 focus:text-emerald-700"
          data-testid={`history-reauction-${player.id}`}
        >
          <ArrowsClockwise size={14} className="mr-2 text-emerald-600" /> Re-auction now
        </DropdownMenuItem>
        {isSold ? (
          <DropdownMenuItem
            onClick={onUnsold}
            className="cursor-pointer focus:bg-amber-500/20 focus:text-amber-600"
            data-testid={`history-unsold-${player.id}`}
          >
            <XCircle size={14} className="mr-2 text-amber-600" /> Mark UNSOLD
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={onAvailable}
            className="cursor-pointer focus:bg-blue-500/20 focus:text-blue-700"
            data-testid={`history-available-${player.id}`}
          >
            <Play size={14} className="mr-2 text-blue-600" /> Send to Available
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function useCountdown(endsAt, status) {
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!endsAt || status !== "live") { setRemaining(0); return; }
    const tick = () => {
      const diff = Math.max(0, Math.floor((new Date(endsAt).getTime() - Date.now()) / 1000));
      setRemaining(diff);
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [endsAt, status]);
  return remaining;
}

function PlayerPicker({ auctionId, onClose, onPick, category }) {
  const [players, setPlayers] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get(`/auctions/${auctionId}/players`).then(({ data }) =>
      setPlayers(data.filter((p) => p.status === "available" || p.status === "unsold"))
    );
  }, [auctionId]);
  const filtered = players.filter((p) => {
    if (category && p.category !== category) return false;
    if (!q) return true;
    const query = q.toLowerCase().trim();
    return (
      p.name.toLowerCase().includes(query) ||
      String(p.player_number || "").includes(query) ||
      (p.role || "").toLowerCase().includes(query) ||
      (p.category || "").toLowerCase().includes(query)
    );
  });
  return (
    <Modal open={true} onClose={onClose} title={`Pick a player${category ? ` · Category ${category}` : ""}`} subtitle="NEXT ON THE FLOOR" size="lg" testId="player-picker">
      <input data-testid="player-picker-search" placeholder="Search by name, #number, role, category…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full bg-white border border-gray-200 focus:border-blue-500 outline-none rounded px-3 py-2 mb-4" />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {filtered.map((p) => (
          <button key={p.id} onClick={() => onPick(p)} data-testid={`pick-player-${p.id}`} className="text-left bg-white border border-gray-200 hover:border-blue-500 rounded p-3 transition-colors">
            <div className="label-mono">{p.player_number ? `#${p.player_number} · ` : ""}{p.role} · Base {p.base_price}</div>
            <div className="font-display text-lg leading-tight">{p.name}</div>
            <div className="text-xs text-gray-500">{p.location || "—"}</div>
          </button>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center text-gray-500 py-8">No available players.</div>}
      </div>
    </Modal>
  );
}

export default function LiveAuction() {
  const { auctionId } = useParams();
  const { user, refresh } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "auctioneer" || user?.role === "superadmin";
  const [state, setState] = useState(null);
  const [teams, setTeams] = useState([]);
  const [flash, setFlash] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pickCategory, setPickCategory] = useState(""); // Iter24: category filter for Pick/Random
  const [selectedBidderId, setSelectedBidderId] = useState("");
  const [customSoldAmount, setCustomSoldAmount] = useState("");
  const [auction, setAuction] = useState(null);
  const [counts, setCounts] = useState({ available: 0, sold: 0, unsold: 0, total: 0 });
  const [historySearch, setHistorySearch] = useState("");

  const loadTeams = useCallback(async () => {
    try { const { data } = await api.get(`/auctions/${auctionId}/teams`); setTeams(data); } catch (e) { /* ignore */ }
  }, [auctionId]);
  const loadState = useCallback(async () => {
    try { const { data } = await api.get(`/auctions/${auctionId}/state`); setState(data); } catch (e) { /* ignore */ }
  }, [auctionId]);
  const loadAuction = useCallback(async () => {
    try { const { data } = await api.get(`/auctions/${auctionId}`); setAuction(data); } catch (e) { /* ignore */ }
  }, [auctionId]);
  const loadCounts = useCallback(async () => {
    try { const { data } = await api.get(`/auctions/${auctionId}/counts`); setCounts(data); } catch (e) { /* ignore */ }
  }, [auctionId]);

  useEffect(() => { loadState(); loadTeams(); loadAuction(); loadCounts(); }, [loadState, loadTeams, loadAuction, loadCounts]);

  // Safety-net polling for state — refreshes every 15 s even if the WebSocket
  // is stuck or dropped by the proxy. Guarantees the Current Bid, Sell-to-team
  // dropdown and bid history never sit stale for more than 15 s regardless of
  // WS health. Prod behind ingress needs this belt-and-suspenders.
  useEffect(() => {
    if (!auctionId) return;
    const id = setInterval(() => { loadState(); loadTeams(); }, 15000);
    return () => clearInterval(id);
  }, [auctionId, loadState, loadTeams]);

  const onWs = useCallback((msg) => {
    // On (re)connect, force a full HTTP re-sync — the WS may have missed events.
    if (msg.type === "__reconnected") { loadState(); loadTeams(); loadCounts(); return; }
    if (msg.state) setState(msg.state);
    if (msg.type === "bid_placed") { setFlash(true); setTimeout(() => setFlash(false), 600); loadTeams(); }
    if (["player_sold", "player_unsold", "auction_skipped", "auction_reset", "sale_cancelled", "player_status_changed", "player_added", "reauction_round", "players_bulk_added"].includes(msg.type)) {
      loadTeams(); loadState(); loadCounts(); refresh();
    }
    if (["auction_started", "auction_paused", "auction_resumed"].includes(msg.type)) loadState();
  }, [loadTeams, loadState, loadCounts, refresh]);

  useAuctionSocket(auctionId, onWs);

  // Keep selected bidder in sync with current bidder unless the auctioneer picked a different one
  useEffect(() => {
    if (state?.current_bidder_team_id) setSelectedBidderId(state.current_bidder_team_id);
  }, [state?.current_bidder_team_id, state?.current_player_id]);

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const startNext = () => call(async () => {
    const params = pickCategory ? `?category=${encodeURIComponent(pickCategory)}` : "";
    const { data } = await api.post(`/auctions/${auctionId}/state/next-random${params}`);
    setState(data);
  });
  const pause = () => call(async () => { await api.post(`/auctions/${auctionId}/state/pause`); });
  const resume = () => call(async () => { await api.post(`/auctions/${auctionId}/state/resume`); });
  const sell = () => call(async () => {
    const winnerId = selectedBidderId || (state?.current_bidder_team_id || "");
    if (!winnerId) { toast.error("Select the winning team first"); return; }
    // Custom price override — organiser can type "20000" to jump from 5 k → 20 k
    // in one click instead of stepping every increment.
    const custom = customSoldAmount ? parseInt(customSoldAmount, 10) : null;
    if (customSoldAmount && (!Number.isFinite(custom) || custom <= 0)) {
      toast.error("Enter a valid custom sold amount (> 0)");
      return;
    }
    const body = { team_id: winnerId };
    if (custom) body.amount = custom;
    const { data } = await api.post(`/auctions/${auctionId}/state/sell-to`, body);
    if (data.result === "sold") toast.success(`${data.player.name} sold to ${data.team.name} @ ${data.amount}`);
    else toast(`Unsold`);
    setSelectedBidderId(""); // reset the SELL TO TEAM dropdown after every sale
    setCustomSoldAmount(""); // clear custom amount after every sale
    loadTeams(); loadState();
  });
  const incrementBid = () => call(async () => { await api.post(`/auctions/${auctionId}/state/increment`, {}); });
  const undoBid = () => call(async () => {
    try {
      await api.post(`/auctions/${auctionId}/state/undo-bid`);
      toast.success("Last bid removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nothing to undo");
    }
  });
  const cancelSale = async (playerId) => {
    if (!window.confirm("Cancel this sale? Player returns to pool and team is refunded.")) return;
    try { await api.post(`/auctions/${auctionId}/cancel-sale/${playerId}`); toast.success("Sale cancelled"); loadTeams(); loadHistory(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const skip = () => call(async () => { await api.post(`/auctions/${auctionId}/state/skip`); toast("Skipped"); });
  const markUnsold = () => call(async () => { await api.post(`/auctions/${auctionId}/state/unsold`); toast("Marked UNSOLD"); loadState(); });
  const reset = () => call(async () => { if (!window.confirm("Reset current auction state?")) return; await api.post(`/auctions/${auctionId}/state/reset`); loadState(); });
  const reauctionRound = () => call(async () => {
    if (!window.confirm("Start a Re-auction Round? All UNSOLD players will be moved back into the available pool.")) return;
    const { data } = await api.post(`/auctions/${auctionId}/reauction`);
    toast.success(`Re-auction round started · ${data.count || 0} players reset`);
    loadHistory();
  });
  const setPlayerStatus = async (playerId, target) => {
    const label = target === "reauction" ? "Re-auction now" : (target === "unsold" ? "Mark UNSOLD" : "Send to Available");
    if (!window.confirm(`${label} — confirm?`)) return;
    try {
      await api.post(`/auctions/${auctionId}/players/${playerId}/status`, { status: target });
      toast.success(label);
      loadHistory(); loadTeams(); loadState();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const pick = (p) => { setShowPicker(false); call(async () => { const { data } = await api.post(`/auctions/${auctionId}/state/start-player/${p.id}`); setState(data); }); };
  const adminBid = useCallback((team, amount) => call(async () => {
    // Amount null → backend auto-computes current + step. Fixes rapid-bid race
    // where the client's cached `nextBid` was a step behind after a peer bid.
    const body = { team_id: team.id };
    if (amount != null) body.amount = amount;
    await api.post(`/auctions/${auctionId}/state/admin-bid`, body);
    // Iter40: always re-sync /teams + /state right after an admin bid so the
    // budgets, bidder badge and history reflect the bid even if the WS
    // broadcast is delayed or dropped.
    loadTeams();
    loadState();
  }), [auctionId, loadTeams, loadState]);
  const myBid = (amount) => call(async () => { await api.post(`/auctions/${auctionId}/state/bid`, { amount }); refresh(); });

  const cur = state?.current_player;
  const bidder = state?.current_bidder_team;
  const currentBid = state?.current_bid || 0;
  const step = state?.next_step || state?.settings?.bid_increment || 10;
  const nextBid = bidder ? currentBid + step : currentBid;
  const timerRemaining = useCountdown(state?.timer_ends_at, state?.status);
  const bidHistory = useMemo(() => (state?.bid_history || []).slice().reverse(), [state]);
  const basePriceDefault = state?.settings?.default_base_price || 10;
  const minPlayers = state?.settings?.min_players_per_team || 11;

  const maxBidFor = useCallback((t) => {
    // remaining_budget - (min_players - players_bought - 1) * base_price
    const bought = t.players_bought || 0;
    const slotsAfter = Math.max(0, minPlayers - bought - 1);
    return Math.max(0, (t.remaining_budget || 0) - slotsAfter * basePriceDefault);
  }, [minPlayers, basePriceDefault]);

  // Global keydown → look up team by hotkey → place next-min bid for that team.
  // Only fires while a player is on the floor + auction is live + user is admin
  // and the keystroke didn't originate from an input/textarea/contenteditable.
  useEffect(() => {
    if (!isAdmin) return;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || t?.isContentEditable) return;
      if (!cur || state?.status !== "live") return;
      const pressed = (e.key || "").toLowerCase();
      if (!pressed) return;
      const team = teams.find((tm) => (tm.hotkey || "").toLowerCase() === pressed);
      if (!team) return;
      e.preventDefault();
      if ((team.players_bought || 0) >= (team.max_players || 15)) {
        toast.error(`${team.short_name}: squad full`);
        return;
      }
      if (maxBidFor(team) < nextBid) {
        toast.error(`${team.short_name}: can't afford ${nextBid}`);
        return;
      }
      adminBid(team, null); // null → backend uses current+step to avoid stale-race
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAdmin, teams, cur, state?.status, nextBid, adminBid, maxBidFor]);

  // Recent Auction History (sold + unsold)
  const [history, setHistory] = useState([]);
  const loadHistory = useCallback(async () => {
    try {
      const [sold, unsold] = await Promise.all([
        api.get(`/auctions/${auctionId}/players?status=sold`),
        api.get(`/auctions/${auctionId}/players?status=unsold`),
      ]);
      const all = [
        ...sold.data.map((p) => ({ ...p, _kind: "sold" })),
        ...unsold.data.map((p) => ({ ...p, _kind: "unsold" })),
      ];
      // Newest first (by _id timestamp fallback if no updated_at)
      all.sort((a, b) => String(b.id).localeCompare(String(a.id)));
      setHistory(all);
    } catch (e) { /* noop */ }
  }, [auctionId]);
  useEffect(() => { loadHistory(); }, [loadHistory, state?.current_player_id, teams.length]);
  const teamMap = useMemo(() => Object.fromEntries(teams.map((t) => [t.id, t])), [teams]);

  const historyFiltered = useMemo(() => {
    if (!historySearch.trim()) return history.slice(0, 100);
    const q = historySearch.trim().toLowerCase();
    return history.filter((p) => {
      const team = p.sold_to ? teamMap[p.sold_to] : null;
      return (
        String(p.player_number || "").includes(q) ||
        (p.name || "").toLowerCase().includes(q) ||
        (team?.name || "").toLowerCase().includes(q) ||
        (team?.short_name || "").toLowerCase().includes(q)
      );
    }).slice(0, 200);
  }, [history, historySearch, teamMap]);

  return (
    <div className="page-dark -mx-4 sm:-mx-6 px-4 sm:px-6 py-6 -my-6 sm:-my-8">
    <div className="space-y-5" data-testid="live-auction-container">
      <Link to={`/auction/${auctionId}/manage`} className="text-xs text-gray-500 hover:text-blue-600 inline-flex items-center gap-1">
        <ArrowLeft size={12} /> Back to manage
      </Link>

      {bidHistory.length > 0 && (
        <div className="border-y border-gray-200 bg-slate-50 py-2">
          <Marquee gradient={false} speed={40}>
            {bidHistory.map((b, i) => (
              <span key={i} className="ticker-item">
                <span className="live-dot" />
                {b.team_short || b.team_name} bid <span className="text-emerald-600">{b.amount}</span> pts
              </span>
            ))}
          </Marquee>
        </div>
      )}

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mono flex items-center gap-2">
            {state?.status === "live" ? <><span className="live-dot" /> LIVE AUCTION</> : "AUCTION CONTROL"}
          </div>
          <h1 className="font-display uppercase text-2xl sm:text-3xl">Nitisoft&apos;s Auction Control System</h1>
          <div className="flex items-center gap-3 mt-2 text-[11px] font-mono text-gray-500" data-testid="auction-counts">
            <span>Total <span className="text-slate-900 font-bold">{counts.total}</span></span>
            <span className="text-gray-400">·</span>
            <span>Remaining <span className="text-emerald-600 font-bold" data-testid="count-remaining">{counts.available}</span></span>
            <span className="text-gray-400">·</span>
            <span>Sold <span className="text-emerald-600 font-bold">{counts.sold}</span></span>
            <span className="text-gray-400">·</span>
            <span>Unsold <span className="text-amber-600 font-bold" data-testid="count-unsold">{counts.unsold}</span></span>
          </div>
        </div>
      </div>

      {/* TEAMS IN AUCTION — full-width above the buttons row per user request */}
      {teams.length > 0 && (
        <div data-testid="teams-in-auction-section">
          <h3 className="font-display uppercase text-lg mb-2">Teams In Auction</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {teams.map((t) => {
              const leading = bidder && bidder.id === t.id;
              const mBid = maxBidFor(t);
              const atMax = (t.players_bought || 0) >= (t.max_players || 15);
              return (
                <div key={t.id} className={`bg-white border rounded-md p-2.5 ${leading ? "border-emerald-500/60" : "border-gray-200"}`} data-testid={`bidder-card-${t.id}`}>
                  <div className="flex items-center gap-2">
                    {t.logo_url ? (
                      <img src={resolveImageUrl(t.logo_url)} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="w-7 h-7 grid place-items-center rounded flex-shrink-0" style={{ background: `${t.color}22`, border: `1px solid ${t.color}55` }}>
                        <Trophy weight="fill" size={13} style={{ color: t.color }} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate text-xs">{t.name}</div>
                      <div className="label-mono text-[9px]">{t.short_name} · {t.players_bought || 0}/{t.max_players}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-base leading-none text-emerald-600">{t.remaining_budget}</div>
                    </div>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-[9px]">
                    <span className="label-mono text-gray-500">MAX</span>
                    <span className={`font-mono font-bold ${atMax ? "text-red-600" : "text-amber-600"}`}>
                      {atMax ? "FULL" : `${mBid} pts`}
                    </span>
                    {leading && <span className="text-emerald-600 label-mono flex items-center gap-1"><span className="live-dot" /> LEAD</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-5">
          {isAdmin && (
            <div className="flex gap-2 flex-wrap items-center" data-testid="auction-actions">
              <select
                value={pickCategory}
                onChange={(e) => setPickCategory(e.target.value)}
                disabled={busy || state?.status === "live"}
                className="bg-white border border-gray-300 rounded px-2 py-1.5 text-xs font-bold uppercase tracking-widest focus:border-blue-500 outline-none disabled:opacity-50"
                data-testid="live-category-filter"
                title="Restrict Pick / Random to a single category"
              >
                <option value="">ALL CATEGORIES</option>
                {["A", "B", "C", "D", "Icon", "Guest"].map((c) => <option key={c} value={c}>Category {c}</option>)}
              </select>
              <button onClick={() => setShowPicker(true)} disabled={busy || state?.status === "live"} className="btn-ghost !px-3 !py-1.5 text-xs" data-testid="pick-player-button">Pick</button>
              <button onClick={startNext} disabled={busy || state?.status === "live"} className="btn-primary !px-3 !py-1.5 text-xs flex items-center gap-1.5" data-testid="start-random-button">
                <Play weight="fill" size={12} /> Random
              </button>
              <button
                onClick={reauctionRound}
                disabled={busy || counts.unsold === 0 || counts.available > 0}
                className="btn-ghost !px-3 !py-1.5 text-xs flex items-center gap-1.5 !border-amber-500/40 !text-amber-600 relative"
                data-testid="reauction-round-button"
                title={
                  counts.available > 0
                    ? `Finish the remaining ${counts.available} available player(s) first`
                    : (counts.unsold ? "Move all UNSOLD players back into the available pool" : "No unsold players yet")
                }
              >
                <ArrowsClockwise size={12} /> Re-auction Round
                {counts.unsold > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold" data-testid="reauction-badge">{counts.unsold}</span>
                )}
              </button>
              <button onClick={reset} disabled={busy} className="btn-ghost !px-2.5 !py-1.5 text-xs flex items-center gap-1" data-testid="reset-button" title="Reset auction state">
                <ArrowsClockwise size={12} />
              </button>
            </div>
          )}

          {!cur ? (
            <div
              className="relative rounded-md overflow-hidden border border-gray-200 aspect-[16/9] flex flex-col items-center justify-center text-center px-6 bg-white shadow-sm"
              data-testid="no-active-player"
            >
              {auction?.banner_url ? (
                <>
                  <img src={resolveImageUrl(auction.banner_url)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" onError={(e) => { e.target.style.display = "none"; }} />
                  <div className="absolute inset-0 bg-white/70 backdrop-blur-[2px]" />
                </>
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-slate-100" />
              )}
              <div className="relative z-10 flex flex-col items-center gap-4">
                <img src={brandLogoFor(auction)} alt="Auction" className="h-20 opacity-95 drop-shadow" />
                <div className="label-mono">NITISOFT AUCTION</div>
                <div className="font-display text-5xl sm:text-6xl uppercase leading-none text-slate-900">Next Player</div>
                {auction?.name && <div className="text-slate-700 mt-1 text-lg">{auction.name}</div>}
                <p className="text-gray-500 text-sm mt-2 max-w-md">
                  {isAdmin ? "Start a random player or pick manually to begin the next call." : "The auctioneer will begin shortly."}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
              <div className="aspect-[3/4] rounded-md overflow-hidden bg-white border border-gray-200 relative" data-testid="active-player-photo">
                <PlayerPhoto src={cur.photo_url} alt={cur.name} aspect="aspect-[3/4]" className="rounded-md">
                  <div className="flex justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-widest bg-black/60 backdrop-blur px-2 py-1 rounded border border-white/10">{cur.role}</span>
                    {state?.status === "paused" && (
                      <span className="text-[10px] font-bold uppercase tracking-widest bg-amber-500/20 backdrop-blur px-2 py-1 rounded border border-amber-500/40 text-amber-600">PAUSED</span>
                    )}
                  </div>
                  <div>
                    <div className="label-mono flex items-center gap-2">
                      {cur.player_number ? (
                        <span className="text-black bg-emerald-400 font-mono font-bold px-1.5 py-0.5 rounded text-[11px]" data-testid={`active-player-number`}>
                          #{cur.player_number}
                        </span>
                      ) : null}
                      <span>CATEGORY {cur.category}</span>
                    </div>
                    <div
                      className="font-display text-4xl uppercase leading-none text-white"
                      style={{ textShadow: "0 2px 6px rgba(0,0,0,0.85), 0 0 3px rgba(0,0,0,0.9)" }}
                    >{cur.name}</div>
                    <div className="text-gray-600 text-sm mt-1">{cur.location || "—"} · {cur.batting_style || "—"} · {cur.bowling_style || "—"}</div>
                  </div>
                </PlayerPhoto>
              </div>

              <div className="flex flex-col gap-4">
                <div className="bg-white border border-gray-200 rounded-md p-5 flex-1 flex flex-col justify-center">
                  <div className="flex items-center justify-between">
                    <div className="label-mono">CURRENT BID</div>
                    {state?.status === "live" && timerRemaining > 0 && (
                      <div className={`label-mono flex items-center gap-1 ${timerRemaining <= 5 ? "text-red-600" : "text-emerald-600"}`}>
                        <Timer size={14} /> {timerRemaining}s
                      </div>
                    )}
                  </div>
                  <div className={`font-display text-7xl md:text-8xl leading-none ${flash ? "bid-flash" : ""} ${bidder ? "text-emerald-600" : "text-slate-900"}`} data-testid="current-bid-display">
                    {currentBid}
                  </div>
                  <div className="label-mono mt-1">POINTS</div>
                  <div className="mt-4">
                    <div className="label-mono">HIGHEST BIDDER</div>
                    {bidder ? (
                      <div className="flex items-center gap-3 mt-2">
                        <div className="w-9 h-9 grid place-items-center rounded" style={{ background: `${bidder.color}22`, border: `1px solid ${bidder.color}55` }}>
                          <Trophy weight="fill" size={16} style={{ color: bidder.color }} />
                        </div>
                        <div>
                          <div className="font-display text-xl leading-none">{bidder.name}</div>
                          <div className="label-mono">{bidder.short_name}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-gray-500 mt-2">No bids yet · base {cur.base_price}</div>
                    )}
                  </div>
                  <div className="label-mono mt-4">NEXT MIN BID · {nextBid} PTS</div>
                </div>

                {isAdmin && state?.status !== "idle" && (
                  <div className="space-y-2" data-testid="auctioneer-controls">
                    {/* BID + UNDO row */}
                    <div className="grid grid-cols-4 gap-2">
                      <button
                        type="button"
                        onClick={incrementBid}
                        disabled={busy || state?.status !== "live"}
                        className="btn-kinetic !py-2 !px-3 flex items-center justify-center gap-1.5 text-sm col-span-3"
                        data-testid="global-bid-button"
                      >
                        <HandArrowUp weight="fill" size={14} /> BID +{step} → {currentBid + step} PTS
                      </button>
                      <button
                        type="button"
                        onClick={undoBid}
                        disabled={busy || state?.status !== "live" || bidHistory.length === 0}
                        className="btn-ghost !py-2 !px-2 flex flex-col items-center justify-center gap-0.5 !border-amber-500/40 !text-amber-600"
                        data-testid="undo-bid-button"
                        title="Undo the last bid (fixes accidental double-clicks)"
                      >
                        <ArrowUUpLeft size={13} />
                        <span className="text-[9px] uppercase tracking-widest leading-none">UNDO</span>
                      </button>
                    </div>

                    {/* Sell block — SOLD button is GREEN */}
                    <div className="bg-white border border-emerald-500/30 rounded p-2.5 space-y-1.5">
                      <div className="label-mono">SELL TO TEAM</div>
                      <select
                        value={selectedBidderId}
                        onChange={(e) => setSelectedBidderId(e.target.value)}
                        data-testid="bidder-select"
                        className="w-full bg-white border border-gray-200 focus:border-blue-500 rounded px-2.5 py-1.5 text-xs text-slate-900 outline-none"
                      >
                        <option value="">— Select winning team —</option>
                        {teams.map((t) => {
                          const mBid = maxBidFor(t);
                          const atMax = (t.players_bought || 0) >= (t.max_players || 15);
                          const disabled = mBid < currentBid || atMax;
                          return (
                            <option key={t.id} value={t.id} disabled={disabled}>
                              {t.short_name} · {t.name} · left {t.remaining_budget} · max {mBid} · {t.players_bought || 0}/{t.max_players}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Custom Sold Amount — optional override for the SOLD button.
                        Empty ⇒ uses state.current_bid (default). Non-empty ⇒
                        overrides sale price (e.g. type 20000 to jump 5 k → 20 k). */}
                    <div className="flex items-center gap-1.5 mt-1" data-testid="custom-sold-row">
                      <span className="label-mono text-red-200/80 flex-shrink-0">CUSTOM SOLD</span>
                      <input
                        type="number"
                        min="1"
                        placeholder={`${currentBid} (default)`}
                        value={customSoldAmount}
                        onChange={(e) => setCustomSoldAmount(e.target.value)}
                        data-testid="custom-sold-input"
                        className="flex-1 bg-white/95 border border-red-500/30 rounded px-2 py-1 text-xs text-slate-900 outline-none focus:border-yellow-400 font-mono"
                      />
                      {customSoldAmount && (
                        <button
                          type="button"
                          onClick={() => setCustomSoldAmount("")}
                          data-testid="custom-sold-clear"
                          className="text-[10px] font-bold uppercase text-red-200 hover:text-white"
                        >
                          ✕
                        </button>
                      )}
                    </div>

                    {/* 4-column action row: SOLD (green) · UNSOLD (red) · PAUSE/RESUME · SKIP */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                      <button
                        onClick={sell}
                        disabled={busy || !selectedBidderId}
                        className="flex items-center justify-center gap-1 rounded px-2 py-1.5 font-bold uppercase tracking-widest bg-emerald-600 hover:bg-emerald-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[11px]"
                        data-testid="sell-button"
                      >
                        <Stop weight="fill" size={12} /> SOLD @ {customSoldAmount || currentBid}
                      </button>
                      <button
                        onClick={markUnsold}
                        disabled={busy}
                        className="flex items-center justify-center gap-1 rounded px-2 py-1.5 font-bold uppercase tracking-widest bg-red-600 hover:bg-red-500 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-[11px]"
                        data-testid="unsold-button"
                      >
                        <XCircle weight="fill" size={12} /> UNSOLD
                      </button>
                      {state?.status === "live" ? (
                        <button onClick={pause} disabled={busy} className="btn-ghost !py-1.5 !px-2 flex items-center justify-center gap-1 text-[11px]" data-testid="pause-button">
                          <Pause weight="fill" size={12} /> Pause
                        </button>
                      ) : (
                        <button onClick={resume} disabled={busy} className="btn-primary !py-1.5 !px-2 flex items-center justify-center gap-1 text-[11px]" data-testid="resume-button">
                          <Play weight="fill" size={12} /> Resume
                        </button>
                      )}
                      <button onClick={skip} disabled={busy} className="btn-ghost !py-1.5 !px-2 flex items-center justify-center gap-1 text-[11px]" data-testid="skip-button">
                        <SkipForward weight="fill" size={12} /> Skip
                      </button>
                    </div>
                  </div>
                )}

                {user?.role === "team_owner" && state?.status === "live" && (
                  <button onClick={() => myBid(nextBid)} disabled={busy || (bidder && bidder.id === user.team_id)} className="btn-kinetic flex items-center justify-center gap-2 text-lg" data-testid="bid-button">
                    <HandArrowUp weight="fill" size={18} />
                    {bidder && bidder.id === user.team_id ? "You're leading" : `Bid ${nextBid} pts`}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* TEAM QUICK-BID BAR — placed OUTSIDE the 2-column player-photo grid so it
              spans the full width and never overlaps the player photo when there are
              more than 6 teams. Click a card (or press its hotkey) to place the
              next-min bid on that team's behalf. */}
          {cur && state?.status === "live" && isAdmin && (
            <TeamQuickBidBar
              teams={teams}
              nextBid={nextBid}
              currentBidderId={bidder?.id}
              busy={busy}
              onBid={(team) => adminBid(team, null)}
              maxBidFor={maxBidFor}
            />
          )}

          {/* TEAMS IN AUCTION section moved to above the buttons row (full width) */}
        </div>

        <aside className="lg:col-span-4 space-y-4">
          {/* RECENT PLAYERS — full history with search + table style */}
          {isAdmin && (
            <div data-testid="auction-history-panel">
              <div className="flex items-center justify-between mb-2 gap-2">
                <h3 className="font-display uppercase text-2xl">Recent Players</h3>
                <div className="label-mono text-[10px] whitespace-nowrap">
                  <span className="text-emerald-600">{counts.sold} Sold</span>
                  {" · "}
                  <span className="text-amber-600">{counts.unsold} Unsold</span>
                </div>
              </div>
              <div className="relative mb-2">
                <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  value={historySearch}
                  onChange={(e) => setHistorySearch(e.target.value)}
                  placeholder="Search #, name, team…"
                  data-testid="history-search-input"
                  className="w-full bg-white border border-gray-200 focus:border-blue-500 rounded pl-9 pr-3 py-2 text-sm text-slate-900 outline-none"
                />
              </div>
              <div className="bg-slate-50 border border-gray-200 rounded overflow-hidden">
                <div className="max-h-[720px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-white text-gray-500 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left label-mono">Sr</th>
                        <th className="px-2 py-2 text-left label-mono">PID</th>
                        <th className="px-2 py-2 text-left label-mono">Player</th>
                        <th className="px-2 py-2 text-left label-mono">State</th>
                        <th className="px-2 py-2 text-right label-mono">Sold</th>
                        <th className="px-2 py-2 text-left label-mono">Team</th>
                        <th className="px-2 py-2 label-mono"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {historyFiltered.length === 0 ? (
                        <tr><td colSpan={7} className="px-2 py-8 text-center text-gray-500">No matching players.</td></tr>
                      ) : historyFiltered.map((p, idx) => {
                        const soldTeam = p.sold_to ? teamMap[p.sold_to] : null;
                        const isSold = p._kind === "sold";
                        return (
                          <tr key={p.id} className="border-t border-gray-100 hover:bg-white/50" data-testid={`history-row-${p.id}`}>
                            <td className="px-2 py-2 text-gray-500 font-mono">{idx + 1}</td>
                            <td className="px-2 py-2 text-gray-500 font-mono">{p.player_number || "—"}</td>
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-2">
                                <img
                                  src={p.photo_url ? resolveImageUrl(p.photo_url) : PHOTO_FALLBACK}
                                  onError={(e) => { e.target.src = PHOTO_FALLBACK; }}
                                  alt=""
                                  className="w-7 h-7 rounded object-cover flex-shrink-0"
                                />
                                <span className="font-semibold text-white">{p.name}</span>
                              </div>
                            </td>
                            <td className="px-2 py-2">
                              {isSold ? (
                                <span className="text-[9px] uppercase tracking-widest bg-emerald-500/20 text-emerald-700 px-1.5 py-0.5 rounded border border-emerald-500/30">SOLD</span>
                              ) : (
                                <span className="text-[9px] uppercase tracking-widest bg-red-500/20 text-red-700 px-1.5 py-0.5 rounded border border-red-500/30">UNSOLD</span>
                              )}
                            </td>
                            <td className="px-2 py-2 text-right font-mono text-emerald-600">
                              {isSold ? p.sold_price : "—"}
                            </td>
                            <td className="px-2 py-2 text-gray-500">
                              {soldTeam ? soldTeam.short_name : "—"}
                            </td>
                            <td className="px-2 py-1 text-right">
                              <HistoryActions
                                player={p}
                                onReauction={() => setPlayerStatus(p.id, "reauction")}
                                onUnsold={() => setPlayerStatus(p.id, "unsold")}
                                onAvailable={() => setPlayerStatus(p.id, "available")}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>

      {showPicker && <PlayerPicker auctionId={auctionId} onClose={() => setShowPicker(false)} onPick={pick} category={pickCategory} />}
    </div>
    </div>
  );
}

/**
 * TeamQuickBidBar — click a team card (or press its assigned hotkey) to place
 * the next-min bid on their behalf via `POST /state/admin-bid`. Cards show
 * team logo, short name, remaining balance and the hotkey label (if any).
 * Disabled + faded when the team can't afford the next bid or is already the
 * current bidder or squad is full.
 */
function TeamQuickBidBar({ teams, nextBid, currentBidderId, busy, onBid, maxBidFor }) {
  if (!teams || teams.length === 0) return null;
  return (
    <div className="mt-2 border-t border-red-500/25 pt-2" data-testid="team-quick-bid-bar">
      <div className="label-mono text-red-200/80 mb-1.5 flex items-center justify-between">
        <span>QUICK BID — TAP TEAM OR PRESS HOTKEY</span>
        <span className="text-red-200/60">Next: <span className="text-yellow-300 font-bold">{nextBid}</span> pts</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-1.5">
        {teams.map((t) => {
          const affordable = maxBidFor(t) >= nextBid;
          const squadFull = (t.players_bought || 0) >= (t.max_players || 15);
          const isCurrent = currentBidderId === t.id;
          const disabled = busy || !affordable || squadFull;
          const hotkey = (t.hotkey || "").toString().toUpperCase();
          return (
            <button
              type="button"
              key={t.id}
              onClick={() => onBid(t)}
              disabled={disabled}
              className={`relative flex items-center gap-2 rounded px-2 py-1.5 text-left transition-all border ${isCurrent ? "border-emerald-400 bg-emerald-500/15" : "border-red-500/40 bg-black/40 hover:bg-red-900/40"} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:scale-[1.02] active:scale-95"}`}
              style={{ borderLeftColor: t.color || "#F97316", borderLeftWidth: 3 }}
              title={squadFull ? "Squad full" : (!affordable ? `Can't afford ${nextBid}` : `Bid ${nextBid} for ${t.name}`)}
              data-testid={`quick-bid-${t.id}`}
            >
              {t.logo_url ? (
                <img
                  src={resolveImageUrl(t.logo_url)}
                  alt=""
                  className="w-9 h-9 rounded object-cover flex-shrink-0 bg-black/40"
                  onError={(e) => { e.target.style.display = "none"; }}
                />
              ) : (
                <div className="w-9 h-9 rounded grid place-items-center flex-shrink-0" style={{ background: `${t.color || "#F97316"}33`, border: `1px solid ${t.color || "#F97316"}66` }}>
                  <Trophy weight="fill" size={16} style={{ color: t.color || "#F97316" }} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="font-bold uppercase text-white text-xs leading-tight truncate">{t.short_name || t.name}</div>
                <div className={`text-[10px] font-mono leading-tight font-bold ${affordable ? "text-emerald-300" : "text-red-400"}`}>
                  {(t.remaining_budget || 0).toLocaleString()}
                </div>
              </div>
              {hotkey && (
                <span
                  className="ml-auto self-start text-[10px] font-mono font-bold bg-yellow-400 text-black px-1.5 rounded flex-shrink-0"
                  style={{ marginTop: -2 }}
                  data-testid={`quick-bid-hotkey-${t.id}`}
                >{hotkey}</span>
              )}
              {isCurrent && (
                <span className="absolute -top-1.5 right-1 text-[8px] font-black bg-emerald-500 text-black px-1 rounded uppercase">Lead</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

